import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import ReactSelect from "react-select";
import Swal from "sweetalert2";

/**
 * Pre-flight human gate for ambiguous Purchased Goods & Services matches.
 *
 * Shown BEFORE the report runs, listing only the products whose activity match is in
 * question (ambiguous/weak, no saved mapping). The user picks an activity per product;
 * on confirm we batch-save the mappings, then the caller runs the report (which now
 * resolves every product deterministically).
 *
 * Props:
 *   show        - boolean
 *   items       - [{ product, category, region, candidates:[{activity_id,title,region,score,unit_type}], suggestedActivityId, unitType }]
 *   userEmail   - logged-in user's email
 *   siteLocation, esgMetric - scope for saving the mappings
 *   onCancel    - () => void  (abort: do not run the report)
 *   onSaved     - () => void  (mappings saved; caller should now run the report)
 */
function MappingGateModal({ show, items, userEmail, siteLocation, esgMetric, onCancel, onSaved }) {
  const [selected, setSelected] = useState({}); // index -> activity_id
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const defaults = {};
    (items || []).forEach((it, i) => {
      defaults[i] = it.suggestedActivityId || (it.candidates && it.candidates[0] && it.candidates[0].activity_id) || "";
    });
    setSelected(defaults);
  }, [items]);

  const optionsFor = (it) =>
    (it.candidates || []).map((c) => ({
      value: c.activity_id,
      label: `${c.title} — ${Math.round((c.score || 0) * 100)}% · ${c.region || "—"}`,
      unit_type: c.unit_type,
    }));

  async function handleConfirm() {
    // Build the batch of chosen mappings.
    const mappings = (items || [])
      .map((it, i) => {
        const activityId = selected[i];
        if (!activityId) return null;
        const cand = (it.candidates || []).find((c) => c.activity_id === activityId);
        return {
          product: it.product,
          category: it.category,
          region: it.region,
          activityId,
          unitType: cand ? cand.unit_type : it.unitType,
        };
      })
      .filter(Boolean);

    if (mappings.length !== (items || []).length) {
      Swal.fire({ icon: "warning", text: "Please choose an activity for every product." });
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BASE_API_URL}SaveActivityMappings`, {
        method: "POST",
        headers: { "Content-type": "application/json", username: userEmail },
        body: JSON.stringify({ userEmail, siteLocation, esgMetric, mappings }),
      });
      const data = await resp.json();
      setSubmitting(false);
      if (data && data.success !== false) {
        onSaved && onSaved();
      } else {
        Swal.fire({ icon: "error", text: (data && data.message) || "Failed to save mappings." });
      }
    } catch (e) {
      setSubmitting(false);
      Swal.fire({ icon: "error", text: "Failed to save mappings. Please retry." });
    }
  }

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered className="reports-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "20px", color: "#407775" }}>
          Confirm activity mappings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: "14px", color: "#707070", marginBottom: "16px" }}>
          A few products matched more than one possible emission factor. Pick the correct activity for each —
          we'll save your choice and then run the report. (Confident and previously-confirmed products are
          handled automatically.)
        </p>
        <div style={{ maxHeight: "440px", overflowY: "auto" }}>
          {(items || []).map((it, i) => (
            <div
              key={`${it.product}-${i}`}
              style={{ border: "1px solid #40777526", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}
            >
              <div style={{ fontSize: "15px", fontWeight: 600, color: "#333" }}>{it.product || "(unnamed)"}</div>
              <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>
                {it.category ? `Category: ${it.category} · ` : ""}{it.region ? `Region: ${it.region}` : ""}
              </div>
              <ReactSelect
                inputId={`map-${i}`}
                value={optionsFor(it).find((o) => o.value === selected[i]) || null}
                onChange={(opt) => setSelected((p) => ({ ...p, [i]: opt ? opt.value : "" }))}
                options={optionsFor(it)}
                placeholder="Select the correct activity…"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
              />
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
          {submitting ? "Saving…" : "Confirm & run report"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MappingGateModal;
